import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";

import { GraphqlErrorMessage } from "../../../components/ErrorMessage";
import { Error404 } from "../../../components/ErrorPage";
import { Loading } from "../../../components/LoadingFork";
import { T } from "../constants";
import Panel from "../components/Panel";
import Plate from "../components/Plate";
import VipPageShell from "../vip/components/VipPageShell";
import { formatCurrency } from "../../../lib/rateCardUtils";
import { bookingById } from "../../Booking/queries";
import { formatDateTime, statusLabel } from "./data";

const useStyles = makeStyles({
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  headerChip: {
    borderRadius: 6,
    border: "1px solid rgba(255,255,255,0.25)",
    color: "rgba(255,255,255,0.86)",
    fontSize: 11,
    letterSpacing: "0.08em",
    padding: "4px 10px",
    textTransform: "uppercase",
  },
  layout: {
    alignItems: "start",
    display: "grid",
    gap: 24,
    gridTemplateColumns: "minmax(0, 1fr) 420px",
    "@media (max-width: 1100px)": { gridTemplateColumns: "1fr" },
  },
  sectionHead: {
    borderBottom: `1px solid ${T.borderSoft}`,
    background: T.surface,
    padding: "12px 20px",
  },
  sectionTitle: { margin: 0, fontSize: 16, fontWeight: 600, color: T.fg },
  backLink: {
    color: T.primary,
    display: "inline-block",
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 14,
    textDecoration: "none",
  },
  rows: {
    columnGap: 24,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    margin: 0,
    padding: 20,
    rowGap: 16,
    "@media (max-width: 760px)": { gridTemplateColumns: "1fr" },
  },
  key: {
    color: T.mutedFg,
    fontSize: 10,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
  },
  value: { color: T.fg, fontSize: 14, margin: "3px 0 0", lineHeight: 1.4 },
  note: {
    background: T.primaryTint,
    border: `1px solid ${T.borderSoft}`,
    borderRadius: 8,
    color: T.fg,
    fontSize: 13,
    gridColumn: "1 / -1",
    lineHeight: 1.5,
    margin: 0,
    padding: 12,
  },
  tabs: { display: "flex", flexWrap: "wrap", gap: 8, padding: 16 },
  tab: {
    border: `1px solid ${T.border}`,
    borderRadius: 6,
    background: T.elevated,
    color: T.fg,
    cursor: "pointer",
    fontFamily: "inherit",
    fontSize: 12,
    fontWeight: 600,
    padding: "8px 12px",
  },
  activeTab: {
    background: T.primary,
    borderColor: T.primary,
    color: T.primaryFg,
  },
  content: { padding: "0 20px 20px" },
  event: {
    borderBottom: `1px solid ${T.borderSoft}`,
    padding: "12px 0",
    "&:last-child": { borderBottom: "none" },
  },
  eventTop: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 },
  eventTitle: { color: T.fg, fontSize: 13, fontWeight: 700 },
  eventTime: { color: T.mutedFg, fontSize: 10, whiteSpace: "nowrap" },
  eventDetail: { color: T.fg, fontSize: 12, lineHeight: 1.45, margin: "6px 0 0" },
  empty: { color: T.mutedFg, fontSize: 13, padding: "8px 0" },
  session: { border: `1px solid ${T.borderSoft}`, borderRadius: 8, marginBottom: 12, padding: 14 },
  sessionTop: { display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12 },
  badge: {
    borderRadius: 6,
    background: T.warningTint,
    color: T.warningFg,
    fontSize: 11,
    fontWeight: 700,
    padding: "4px 8px",
  },
});

function Detail({ label, value }) {
  const s = useStyles();

  return (
    <div>
      <dt className={s.key}>{label}</dt>
      <dd className={s.value}>{value || "—"}</dd>
    </div>
  );
}

const dateValue = (value) => formatDateTime(value);
const sortDesc = (a, b) => new Date(b.at || 0) - new Date(a.at || 0);

function emailStatus(email) {
  if (!email.finalisedAt) return "PENDING";

  return email.mailgunId?.startsWith("Error:") ? "ERROR" : "SUCCESS";
}
function alertDetail(item) {
  const timestamps = [
    item.sentAt && `sent ${dateValue(item.sentAt)}`,
    item.deliveredAt && `delivered ${dateValue(item.deliveredAt)}`,
    item.failedAt && `failed ${dateValue(item.failedAt)}`,
  ].filter(Boolean).join(" · ");
  const error = item.errorMessage ? ` · ${item.errorMessage}` : "";

  return `${item.providerType || item.alertType || "Alert"} · ${statusLabel(
    item.status,
  )}${timestamps ? ` · ${timestamps}` : ""}${error}`;
}

function VipBooking({ match, installation }) {
  const s = useStyles();
  const bookingId = match.params.bookingId || match.params.alertId;
  const conciergeUrl = match.url.replace(/\/vip-booking\/[^/]+$/, "");
  const [tab, setTab] = useState("all");
  const bookingResult = useQuery(bookingById, {
    variables: {
      bookingId,
      includeParkingSessions: true,
      includeVipAlertLogs: true,
    },
    skip: !bookingId,
    fetchPolicy: "network-only",
  });
  const booking = bookingResult.data?.booking;
  const alerts = booking?.alertLogs || [];

  const events = booking?.events || [];
  const sessions = booking?.parkingSessions || [];
  const emails = booking?.emails || [];
  const lastEntry = events
    .filter((event) => event.gateType === "ENTRY")
    .sort((a, b) => new Date(b.pcLoggedAt) - new Date(a.pcLoggedAt))[0]?.pcLoggedAt;
  const lastExit = events
    .filter((event) => event.gateType === "EXIT")
    .sort((a, b) => new Date(b.pcLoggedAt) - new Date(a.pcLoggedAt))[0]?.pcLoggedAt;
  const allEvents = useMemo(
    () => [
      {
        at: booking?.createdAt,
        title: "Booking created",
        detail: "Booking was created.",
      },
      booking?.currentAt
        ? {
            at: booking.currentAt,
            title: "Booking current",
            detail: "Booking became current.",
          }
        : null,
      booking?.closedAt
        ? {
            at: booking.closedAt,
            title: "Booking closed",
            detail: "Booking was closed.",
          }
        : null,
      booking?.finalisedAt
        ? {
            at: booking.finalisedAt,
            title: "Booking finalised",
            detail: "Booking was finalised.",
          }
        : null,
      ...events.map((event) => ({
        at: event.pcLoggedAt,
        title: `${statusLabel(event.gateType)} event`,
        detail: event.gateDescription || "Parking event",
      })),
      ...emails.map((email) => ({
        at: email.createdAt || email.finalisedAt,
        title: "Email enqueued",
        detail: `${email.templateClass || "Email"} · ${emailStatus(email)}${
          email.mailgunId?.startsWith("Error:")
            ? ` · ${email.mailgunId.slice(6).trim()}`
            : ""
        }`,
      })),
      ...alerts.map((item) => ({
        at: item.createdAt,
        title: "VIP alert",
        detail: alertDetail(item),
      })),
    ]
      .filter(Boolean)
      .sort(sortDesc),
    [booking, events, emails, alerts],
  );

  if (bookingResult.loading) return <Loading />;
  if (bookingResult.error) return <GraphqlErrorMessage error={bookingResult.error} />;
  if (!booking) return <Error404 />;
  const paidCharges = (booking?.charges || []).filter((charge) => charge.finalisedAt && !charge.error);
  const totalPaid = paidCharges.reduce((total, charge) => total + (Number(charge.amountAud) || 0), 0);
  const labels = {
    all: "All Events",
    sessions: "Parking sessions",
    events: "Events",
    emails: "Emails",
    alerts: "VIP alerts",
  };
  const countFor = (key) =>
    key === "all"
      ? allEvents.length
      : ({ sessions, events, emails, alerts }[key] || []).length;
  const tabItems =
    tab === "all"
      ? allEvents
      : tab === "sessions"
        ? sessions
        : tab === "events"
          ? events.map((event) => ({
              at: event.pcLoggedAt,
              title: `${statusLabel(event.gateType)} event`,
              detail: event.gateDescription || "Parking event",
            }))
          : tab === "emails"
            ? emails.map((email) => ({
                at: email.createdAt || email.finalisedAt,
                title: "Email enqueued",
                detail: `${email.templateClass || "Email"} · ${emailStatus(email)}${
                  email.mailgunId?.startsWith("Error:")
                    ? ` · ${email.mailgunId.slice(6).trim()}`
                    : ""
                }`,
              }))
            : alerts.map((item) => ({
                at: item.createdAt,
                title: "VIP alert",
                detail: alertDetail(item),
              }));

  return (
    <VipPageShell
      eyebrow="Concierge console - Booking detail"
      title="Booking (VIP)"
      maxWidth={1200}
      right={
        <div className={s.headerRight}>
          <span className={s.headerChip}>{booking?.status || "-"}</span>
          <span className={s.headerChip}>{booking?.rateCard?.name || "-"}</span>
        </div>
      }
    >
      <Link to={`${conciergeUrl}/vip-alerts`} className={s.backLink}>
        Back to VIP Alert Logs
      </Link>
      <div className={s.layout}>
        <Panel>
          <div className={s.sectionHead}>
            <h2 className={s.sectionTitle}>Main info</h2>
          </div>
          <dl className={s.rows}>
            <Detail label="Guest" value={booking?.guestName} />
            <Detail
              label="Licence plate"
              value={<Plate>{booking?.plate || "—"}</Plate>}
            />
            <Detail label="Mobile" value={booking?.guestPhone} />
            <Detail label="Email" value={booking?.guestEmail} />
            <Detail label="Guest card" value={booking?.rateCard?.name} />
            <Detail label="Status" value={statusLabel(booking?.status)} />
            <Detail label="Start" value={dateValue(booking?.startDay)} />
            <Detail label="End" value={dateValue(booking?.endDay)} />
            <Detail label="Last Entry" value={dateValue(lastEntry)} />
            <Detail label="Last Exit" value={dateValue(lastExit)} />
            <Detail label="Lot" value={booking?.rateCard?.lot?.name} />
            <Detail
              label="Total paid"
              value={formatCurrency(totalPaid, installation?.currency || "AUD")}
            />
            <Detail
              label="VIP"
              value={booking?.isVip ? "★ VIP booking" : "—"}
            />
            <Detail
              label="Arrival type"
              value={
                booking?.rateCard?.isValet
                  ? "Valet arrival"
                  : "Standard arrival"
              }
            />
            <Detail
              label="Contacts notified"
              value={alerts
                .map((item) => item.contact?.name)
                .filter(Boolean)
                .join(", ")}
            />
            {booking?.carNotes ? (
              <div className={s.note}>
                <strong>VIP notes</strong>
                <br />
                {booking.carNotes}
              </div>
            ) : null}
          </dl>
        </Panel>
        <Panel>
          <div className={s.sectionHead}>
            <h2 className={s.sectionTitle}>History</h2>
          </div>
          <div className={s.tabs}>
            {Object.keys(labels).map((key) => (
              <button
                key={key}
                type="button"
                className={`${s.tab} ${tab === key ? s.activeTab : ""}`}
                onClick={() => setTab(key)}
              >
                {labels[key]} ({countFor(key)})
              </button>
            ))}
          </div>
          <div className={s.content}>
            {tab === "sessions" ? (
              <Sessions sessions={sessions} booking={booking} />
            ) : (
              <List items={tabItems} />
            )}
          </div>
        </Panel>
      </div>
    </VipPageShell>
  );
}

function List({ items }) {
  const s = useStyles();
  const sorted = [...items].sort(sortDesc);

  return sorted.length ? (
    sorted.map((item, index) => (
      <div className={s.event} key={`${item.title}-${item.at}-${index}`}>
        <div className={s.eventTop}>
          <span className={s.eventTitle}>{item.title}</span>
          <span className={s.eventTime}>{dateValue(item.at)}</span>
        </div>
        <p className={s.eventDetail}>{item.detail}</p>
      </div>
    ))
  ) : (
    <div className={s.empty}>No records found.</div>
  );
}

function Sessions({ sessions, booking }) {
  const s = useStyles();
  const sorted = [...sessions].sort(
    (a, b) =>
      new Date(b.createdAt || b.entryAt || 0) -
      new Date(a.createdAt || a.entryAt || 0),
  );

  return sorted.length ? (
    sorted.map((session) => (
      <div className={s.session} key={session.id}>
        <div className={s.sessionTop}>
          <strong>
            {session.plate ||
              session.normalizedPlate ||
              booking?.plate ||
              "Unknown plate"}
          </strong>
          <span className={s.badge}>{statusLabel(session.status)}</span>
        </div>
        <div className={s.eventDetail}>
          Entry: {dateValue(session.entryAt)} · {session.entryGateDescription ||
            session.entryGateId ||
            "Unknown gate"}
        </div>
        <div className={s.eventDetail}>
          Exit: {dateValue(session.exitAt)} · {session.exitGateDescription ||
            session.exitGateId ||
            "Unknown gate"}
        </div>
        <div className={s.eventDetail}>
          Minutes parked: {session.parkedMinutes ?? "—"}
        </div>
      </div>
    ))
  ) : (
    <div className={s.empty}>No parking sessions recorded.</div>
  );
}

export default VipBooking;
